import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/ui/design-system/components/ui/collapsible';
import {
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@/ui/design-system/components/ui/sidebar';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';
import { useEntity } from 'utils/hooks/api';
import { useParams, useRouteMatch } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { GiFactory } from 'react-icons/gi';
import Link from '../Link';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import { useIndustries } from 'utils/hooks/useUserIndustries';

function SidebarIndustriesMenu({ isOpen: controlledOpen, onToggle }) {
    const industriesQuery = useIndustries();
    const { industryName, categoryId } = useParams();
    const match = useRouteMatch('/category:level/:categoryId');
    const defaultOpen = industryName || (categoryId && match);
    const { dataSource } = useDataSourceToggle();
    const currentCategory = useEntity({ id: categoryId, dataSource }).data;
    const getHierarchy = useGetHierarchy();
    const { hierarchy_hint } = getHierarchy(currentCategory?.hierarchies);
    const maybeRootCategoryName = hierarchy_hint
        ? hierarchy_hint.cat1
        : industryName;
    const { state, setOpen } = useSidebar();
    const open = controlledOpen ?? !!defaultOpen;

    const handleTriggerClick = (e) => {
        if (state === 'collapsed') {
            e.preventDefault();
            onToggle?.(true);
            setOpen(true);
        }
    };

    const industries = industriesQuery.data
        ? industriesQuery.data.map((industry) => ({
              isActive:
                  maybeRootCategoryName === industry.name &&
                  (industryName || !!match),
              children: industry.name,
              to: industry.to,
              key: industry.id,
              mixpanelContext: `${industry.name} Industry Overview`,
          }))
        : null;

    return (
        <Collapsible
            open={open}
            onOpenChange={onToggle}
            className="group/collapsible"
        >
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip="Industries"
                        onClick={handleTriggerClick}
                    >
                        <GiFactory />
                        <span>Industries</span>
                        <FiChevronDown className="ml-auto !text-sidebar-foreground transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {industries ? (
                            industries.map(
                                ({
                                    key,
                                    children,
                                    isActive,
                                    to,
                                    mixpanelContext,
                                }) => (
                                    <SidebarMenuSubItem key={key}>
                                        <SidebarMenuSubButton
                                            asChild
                                            isActive={!!isActive}
                                        >
                                            <Link
                                                to={to}
                                                mixpanelContext={
                                                    mixpanelContext
                                                }
                                            >
                                                <span className="capitalize">
                                                    {children}
                                                </span>
                                            </Link>
                                        </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                ),
                            )
                        ) : industriesQuery.isLoading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <SidebarMenuSubItem key={`skeleton-${i}`}>
                                    <SidebarMenuSkeleton />
                                </SidebarMenuSubItem>
                            ))
                        ) : (
                            <SidebarMenuSubItem>
                                <span className="px-2 py-1 text-xs text-muted-foreground">
                                    Could not load industries.
                                </span>
                            </SidebarMenuSubItem>
                        )}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

export default SidebarIndustriesMenu;
